export interface Fields {
  key: string;
  value: string;
}

interface PopupId {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
}

/**
 *  Create URL with query parameters based on a set of key - value pairs.
 *
 * @param {string} url - Original URL to which the params will be appended.
 * @param {object[]} fields - Data to send via query params. The objects
 *  in the given array must have the following structure: { key, value }
 *
 * @returns URL with query parameters.
 */
const buildURLwithQueryParams = (url: string, fields: Fields[]): string => {
  const stringifiedQueryParams = fields.map((field) => `${field.key}=${field.value}`).join("&");

  return `${url}?${stringifiedQueryParams}`;
};

/**
 *  Add UTM fields to the destination URL.
 *
 * @param {string} url - URL to which the link redirects to.
 * @param {object} popupId - Object containing properties with the different
 *  UTM parameters: utmSource, utmMedium, utmCampaign, utmContent.
 *
 * @returns URL with UTM codes sent via query params.
 */
const buildURLwithUTMParams = (
  url: string,
  { utmSource, utmMedium, utmCampaign, utmContent }: PopupId,
): string => {
  const fields = [
    { key: "utm_source", value: utmSource },
    { key: "utm_medium", value: utmMedium },
    { key: "utm_campaign", value: utmCampaign },
    { key: "utm_content", value: utmContent },
  ];

  return buildURLwithQueryParams(url, fields);
};

/**
 *  Replaces placeholder strings of type ":param" in an url by the values
 * specified in the "pathParams" object.
 *
 * @param {string} url - URL that includes path parameter names in ":param"
 *  format.
 *
 * @param {object} pathParams - Object where each key represents the name of
 *  the path parameter, and the value for each of those properties is the
 *  value that shall replace the placeholder in the generated URL.
 *
 * @example
 *  // This code will return '/tags/cats'
 *  buildRouteWithPathParams('/tags/:tagId', { tagId: 'cats' })
 *
 * @returns {string} URL with values assigned to each path param.
 */
const buildUrlWithPathParams = (
  url: string,
  pathParams: Record<string, string | number> = {},
): string => {
  let generatedUrl = url;
  for (const name of Object.keys(pathParams)) {
    generatedUrl = generatedUrl.replace(new RegExp(`:${name}`, "gi"), String(pathParams[name]));
  }
  return generatedUrl;
};

export interface QueryParams {
  [key: string]: string | string[] | null;
}

export { buildURLwithQueryParams, buildURLwithUTMParams, buildUrlWithPathParams };
